import { glMatrix, type ReadonlyVec2, vec2 } from 'gl-matrix';

import { FlyweightPool } from '@canvas/object-pool/flyweight-pool';

import { clamp } from '../numbers.ts';

// Flyweight vector pool to avoid GC pressure
const pool = new FlyweightPool<vec2>({
  initializer: () => vec2.create(),
  capacity: 10,
});

/**
 * Finds the closest point on a line segment to a target point.
 *
 * Visual examples:
 * ```
 * Case 1: Point projects onto the line segment
 *
 *    ⦿ target point
 *    │
 *    │ (perpendicular distance)
 *    │
 * A ●─X─────────● B
 *     │
 *     closest point (X)
 *
 * Case 2: Point is beyond the end of the segment
 *
 *                    ⦿ target point
 *                   ╱
 *                  ╱ (distance to endpoint)
 *                 ╱
 * A ●─────────────X B
 *                 │
 *                 closest point = B
 *
 * Case 3: Point is before the start of the segment
 *
 * ⦿ target point
 *  ╲
 *   ╲ (distance to endpoint)
 *    ╲
 * A X─────────────● B
 *   │
 *   closest point = A
 * ```
 *
 * @param start the start of the line segment
 * @param end the end of the line segment
 * @param targetPoint the point we want to find the closest approach to
 * @param out Optional output vector for the closest point
 * @returns The proportion along the line where the closest point lies (0 = start, 1 = end)
 */
export function getClosestPointAlongLine(
  start: ReadonlyVec2,
  end: ReadonlyVec2,
  targetPoint: ReadonlyVec2,
  out?: vec2,
): number {
  // Get direction vector from start to end
  const segmentDirection = vec2.sub(pool.get(), end, start);

  // Get vector from line start to target point
  const pointFromStart = vec2.sub(pool.get(), targetPoint, start);

  // Calculate the length of the segment squared instead of sqrt for perf wins
  const segmentLengthSquared = vec2.sqrLen(segmentDirection);

  if (glMatrix.equals(segmentLengthSquared, 0)) {
    // When the line has no length, the closest point is always the start/end point
    if (out) {
      vec2.copy(out, start);
    }

    return 0;
  }

  // Calculate how far along the segment the closest point is
  // This is the dot product divided by segment length squared
  const unboundedProportion =
    vec2.dot(pointFromStart, segmentDirection) / segmentLengthSquared;

  // Clamp to segment bounds [0, 1]
  const proportion = clamp(0, 1, unboundedProportion);

  // Calculate the actual closest point if requested
  if (out) {
    vec2.scaleAndAdd(out, start, segmentDirection, proportion);
  }

  return proportion;
}

/**
 * Tests if a point is within a certain distance of a line segment.
 * This is faster than calculating the exact distance when you only need a yes/no answer.
 *
 * Visual:
 * ```
 * Points within distance threshold:
 *
 *   ⦿ ← within distance
 *   │
 * A ●─────────● B
 *   │
 *   ⦿ ← within distance
 *
 * Points outside distance threshold:
 *
 * ⦿ ← too far
 *
 * A ●─────────● B
 *
 *                 ⦿ ← too far
 * ```
 *
 * @param start The start of the line segment
 * @param end The end of the line segment
 * @param targetPoint The point to test
 * @param maxDistance Maximum distance to consider "within range"
 * @returns true if the point is within maxDistance of the line segment
 */
export function isPointWithinDistanceOfLine(
  start: ReadonlyVec2,
  end: ReadonlyVec2,
  targetPoint: ReadonlyVec2,
  maxDistance: number,
): boolean {
  const maxDistanceSquared = maxDistance * maxDistance;

  // Fast AABB (bounding box) check to quickly rule out distant points. This check
  // improves average performance by ~50%.
  const lineMinX = Math.min(start[0], end[0]);
  const lineMaxX = Math.max(start[0], end[0]);
  const lineMinY = Math.min(start[1], end[1]);
  const lineMaxY = Math.max(start[1], end[1]);

  // How far is the point outside the box on each axis?
  const dx = Math.max(0, Math.max(lineMinX - targetPoint[0], targetPoint[0] - lineMaxX));
  const dy = Math.max(0, Math.max(lineMinY - targetPoint[1], targetPoint[1] - lineMaxY));

  // Early exit if the point is too far from the bounding box
  if (dx * dx + dy * dy > maxDistanceSquared) {
    return false;
  }

  // Find the closest point on the line segment to the target point
  const closestPoint = pool.get();
  getClosestPointAlongLine(start, end, targetPoint, closestPoint);

  // Calculate squared distance from target point to closest point
  const pointFromStart = vec2.sub(pool.get(), targetPoint, closestPoint);
  const distanceSquared = vec2.sqrLen(pointFromStart);

  // Compare with squared max distance to avoid expensive sqrt
  return distanceSquared <= maxDistanceSquared;
}
